/**
 * Marital Attestation Agent — API client for the per-party marital-status
 * confirmation on a deal in a marital-property state (Wisconsin).
 *
 * Each party (buyer and seller) confirms THEIR OWN marital status. When
 * married, they name their spouse and give the spouse's email and mobile
 * phone; DocuSign Phone Authentication uses that phone to verify a spouse who
 * signs by email. The backend refuses to send the agreement until both
 * parties have confirmed (reasons surface via send-readiness).
 *
 * Backend contract:
 *   GET /api/offers/{offer_id}/marital-attestations
 *     -> { offer_id, required, my_role, attestations: [ {
 *          party_role, person_id, marital_status: 'single'|'married',
 *          spouse_name, spouse_email, spouse_phone_last4,
 *          spouse_phone (full number, only on the caller's own row),
 *          attested_at } ] }
 *   PUT /api/offers/{offer_id}/marital-attestation
 *     body { marital_status, spouse_name?, spouse_email?, spouse_phone? }
 *     -> the caller's row. 422 `detail` on validation errors; 409 `detail`
 *        once the agreement was sent (status is locked).
 */

import APIAgent from './apiAgent';

export const MARITAL_STATUS = Object.freeze({
  SINGLE: 'single',
  MARRIED: 'married',
});

/**
 * Load both parties' marital-status confirmations for an offer.
 *
 * @param {number|string} offerId
 * @returns {Promise<{offer_id: number, required: boolean, my_role: 'buyer'|'seller', attestations: Array<object>}>}
 */
export async function getMaritalAttestations(offerId) {
  try {
    return await APIAgent.request(
      `/api/offers/${encodeURIComponent(offerId)}/marital-attestations`,
      'GET',
    );
  } catch (err) {
    console.error('maritalAttestationAgent.getMaritalAttestations failed:', err);
    throw err;
  }
}

/**
 * Save (create or update) the caller's own marital-status confirmation.
 * Spouse fields are only sent for a married status.
 *
 * Rejects with an Error whose `message` is the server `detail` and whose
 * `status` is the HTTP status (422 validation, 409 locked after send).
 *
 * @param {number|string} offerId
 * @param {{marital_status: 'single'|'married', spouse_name?: string, spouse_email?: string, spouse_phone?: string}} body
 * @returns {Promise<object>} the caller's attestation row
 */
export async function saveMaritalAttestation(offerId, body) {
  const payload = { marital_status: body?.marital_status };
  if (payload.marital_status === MARITAL_STATUS.MARRIED) {
    payload.spouse_name = body.spouse_name;
    payload.spouse_email = body.spouse_email;
    payload.spouse_phone = body.spouse_phone;
  }
  try {
    return await APIAgent.request(
      `/api/offers/${encodeURIComponent(offerId)}/marital-attestation`,
      'PUT',
      payload,
    );
  } catch (err) {
    console.error('maritalAttestationAgent.saveMaritalAttestation failed:', err);
    throw err;
  }
}

const MaritalAttestationAPI = {
  getMaritalAttestations,
  saveMaritalAttestation,
};

export default MaritalAttestationAPI;
